<template>
  <div class="mb-8 flex" v-if="showNavigation">
    <a :href="searchUrl" v-if="searchUrl" class="flex items-center gap-1">
      <svg width="10" height="10" focusable="false" aria-hidden="true">
        <use xlink:href="#arrow-left" />
      </svg>
      {{ t('searchResult') }}</a
    >
    <div class="ml-auto flex gap-4 text-xs uppercase md:gap-6">
      <a
        :href="objectUrl(prev)"
        v-if="prev"
        class="flex items-center gap-1"
        @click="decIndex"
      >
        <svg width="10" height="10" focusable="false" aria-hidden="true">
          <use xlink:href="#arrow-left" />
        </svg>
        {{ t('previous') }}</a
      >
      <a
        :href="objectUrl(next)"
        v-if="next"
        class="flex items-center gap-1"
        @click="incIndex"
        >{{ t('next') }}
        <svg
          width="10"
          height="10"
          style="transform: rotate(180deg)"
          focusable="false"
          aria-hidden="true"
        >
          <use xlink:href="#arrow-left" />
        </svg>
      </a>
    </div>
  </div>
</template>

<i18n>
{
  "en": {
    "previous": "Previsous",
    "next": "Next",
    "searchResult": "Search result"
  },
  "de": {
    "previous": "Zurück",
    "next": "Vor",
    "searchResult": "Suchergebnis"
  }
}
</i18n>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, type Ref } from 'vue';
import { lang } from '../utils/utils';
import { getObjectIds } from '../utils/objects';
import { createApi } from '../utils/gql';
import { useI18n } from 'vue-i18n';
export default defineComponent({
  name: 'ObjectNavigationComponent',

  props: {
    objectId: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const siblings: Ref<string[]> = ref([]);
    const index: Ref<number> = ref();
    const searchUrl: Ref<string> = ref();
    const filter: Ref<object> = ref();
    const sort: Ref<string> = ref();
    const prev = ref('');
    const next = ref('');

    const objectUrl = (id) =>
      lang === 'de' ? `/objekt/${id}` : `/en/object/${id}`;

    onMounted(() => {
      siblings.value = JSON.parse(sessionStorage.getItem('siblingIds') || '[]');
      index.value = parseInt(sessionStorage.getItem('index'), 10);
      searchUrl.value = sessionStorage.getItem('searchUrl');
      filter.value = JSON.parse(sessionStorage.getItem('filter') || 'null');
      sort.value = sessionStorage.getItem('sort');

      directSiblings();
    });

    const showNavigation = computed(
      () => siblings.value.length > 0 || searchUrl.value
    );

    const fetchObjectIds = async () => {
      const api = createApi();
      const response = await getObjectIds(
        api,
        lang,
        filter.value,
        { skip: Math.max(0, index.value - 1), pageSize: 3 },
        sort.value
      );
      return response.objects.map((o) => o.id);
    };

    const computePrevNext = (ids: string[]) => {
      const currentIndex = ids.indexOf(props.objectId);
      const p = currentIndex > 0 ? ids[currentIndex - 1] : null;
      const n =
        currentIndex >= 0 && currentIndex < ids.length - 1
          ? ids[currentIndex + 1]
          : null;

      if (!p && !n) {
        // We are obviously on a random object page without siblings. Delete sessionStorage item.
        sessionStorage.removeItem('siblingIds');
      }

      return {
        p,
        n,
      };
    };

    const directSiblings = () => {
      if (searchUrl.value) {
        fetchObjectIds().then((ids) => {
          const { p, n } = computePrevNext(ids);
          prev.value = p;
          next.value = n;
        });
      }
      if (siblings.value.length) {
        const { p, n } = computePrevNext(siblings.value);
        prev.value = p;
        next.value = n;
      }
    };

    const decIndex = () => {
      sessionStorage.setItem('index', Math.max(index.value - 1, 0));
    };
    const incIndex = () => {
      sessionStorage.setItem('index', index.value + 1);
    };

    return {
      showNavigation,
      next,
      prev,
      objectUrl,
      searchUrl,
      decIndex,
      incIndex,
      ...useI18n(),
    };
  },
});
</script>
